import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import Loader from 'components/Loader';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import SubSection from 'components/section-menu/SubSection';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import { errorAlert } from 'utils/alert';

import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode, useDraftField } from 'components/drafts/DraftSaveMode';
import VacationButton from 'components/employee/VacationButton';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation.tsx';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { useRef } from 'react';
import { Control, FieldValues, FormProvider, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { useAddEmployeeMutation, useUpdateEmployeeMutation } from 'store/api/employees';
import { CustomField } from 'types/customFields';
import { EmployeeForm } from 'types/employees';
import { SystemRole } from 'types/systemRoles';
import { t } from 'utils/getTranslations';
import { formatEmployeeName } from 'utils/nameOrder';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import { employeeFormToRequest, employeeFormToUpdateRequest } from './employeeFormMapping';
import {
    ActiveAndRolesFields,
    AffiliationsFields,
    CanImpersonateField,
    CompetenceFields,
    ContactDetailsFields,
    EmployeeTargetsFields,
    EmploymentDetailsFields,
    EmploymentHistoryFields,
    PeerGroupField,
    PersonalFields,
    PictureAndNameFields,
    PreviousNamesField,
    TaskReceiversField,
    WebIntegrationFields,
} from './form-sections/EmployeeFields';
import { useEmployeeForm } from './form-sections/useEmployeeForm';
import { validateEmploymentHistory } from './util';
import { useSaveWebPicture } from './views/useSaveWebPicture';

type Props = {
    type: 'add' | 'edit';
    initialData?: EmployeeForm;
    refetch?: () => void;
    systemRoles?: SystemRole[];
    entityCustomFields?: CustomField[];
    isEditingCurrentUserAndHasPermissionToUpdateEmployees?: boolean;
    onVacation?: boolean | null;
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    // General
    profilePictureName: 'employees.general',
    signaturePictureName: 'employees.general',
    firstName: 'employees.general',
    localFirstName: 'employees.general',
    lastName: 'employees.general',
    localLastName: 'employees.general',
    personTitle: 'employees.general',
    isActive: 'employees.general',
    systemRoles: 'employees.general',
    workEmailAddress: 'employees.general',
    mobilePhone: 'employees.general',
    workPhone: 'employees.general',
    manager: 'employees.general',
    vatRegNum: 'employees.general',
    overwriteSignedByEmployeeId: 'employees.general',
    employmentStartDate: 'employees.general',
    employmentLeaveDate: 'employees.general',
    notes: 'employees.general',
    previousNames: 'employees.general',
    taskReceivers: 'employees.general',
    canImpersonate: 'employees.general',
    // Competence
    employmentHistory: 'employees.competence',
    supportTeams: 'employees.competence',
    chairmanTeams: 'employees.competence',
    practiceAreas: 'employees.competence',
    languagesSpoken: 'employees.competence',
    partnershipPeerGroup: 'employees.competence',
    affiliations: 'employees.competence',
    // Personal
    genderType: 'employees.personal',
    personalIdCode: 'employees.personal',
    birthdayDate: 'employees.personal',
    sensitiveInfo: 'employees.personal',
    isPoliticallyExposedPerson: 'employees.personal',
    personalAddress: 'employees.personal',
    annualTargetHours: 'employees.personal',
    plannedWorkdaysOff: 'employees.personal',
};

const EmployeePageForm = ({
    type = 'add',
    initialData,
    refetch,
    systemRoles,
    entityCustomFields,
    isEditingCurrentUserAndHasPermissionToUpdateEmployees = false,
    onVacation,
}: Props): JSX.Element => {
    useFocusedEditMode('employees');
    const { id: employeeId } = useCreateRoute().params;
    const form = useEmployeeForm({
        type,
        initialData,
        systemRoles,
        entityCustomFields,
        isEditingCurrentUserAndHasPermissionToUpdateEmployees,
    });
    const {
        methods,
        canReadPersonalData,
        hasUpdateEmployees,
        showTargets,
        lastNameFirst,
        customFields,
        mapCustomFieldsToDto,
        profilePictureUploadRef,
    } = form;
    // A user with only UPDATE.EMPLOYEE_PERSONAL.ALL (not UPDATE.EMPLOYEES) can edit personal fields
    // only — hide the general/competence sections so they're not confused by readonly fields they
    // can never touch. Always show in 'add' mode since that requires UPDATE.EMPLOYEES anyway.
    const showNonPersonalSections = type === 'add' || hasUpdateEmployees;
    const returnTo = useReturnToLocation(employeeId ? `/employee/${employeeId}` : '/employees');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    useCreateDialogForm(methods);
    const { handleSubmit, control, setValue, reset, formState } = methods;

    const uploadProfilePicture = async (formData: EmployeeForm) => {
        if (profilePictureUploadRef.current) {
            const uploadedFileName = await profilePictureUploadRef.current();
            if (uploadedFileName) {
                formData.profilePictureName = uploadedFileName;
            }
        }
    };
    /** The create body, once the chosen profile picture is uploaded. */
    const toCreateRequest = async (formData: EmployeeForm) => {
        await uploadProfilePicture(formData);
        return employeeFormToRequest(formData, mapCustomFieldsToDto(formData as unknown as Record<string, unknown>));
    };
    const draft = useCreateDraft({
        record: 'employee',
        methods: {
            // The form sets an error itself while the employment history is empty: a missing value, which a draft
            // save lets through as it does any required field. An overlap still refuses it.
            handleSubmit: (onValid, onInvalid) => (event) => {
                if (!methods.getValues('employmentHistory')?.length) {
                    methods.clearErrors('employmentHistory');
                }
                return handleSubmit(onValid, onInvalid)(event);
            },
            formState: methods.formState,
        },
        toPayload: toCreateRequest,
    });
    const { errors } = formState;

    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);

    const [addEmployee] = useAddEmployeeMutation();
    const [updateEmployee] = useUpdateEmployeeMutation();
    const saveWebPicture = useSaveWebPicture(employeeId ?? '');

    const bypassNavigationBlockerRef = useRef(false);

    const onSubmit = async (formData: EmployeeForm, action: 'create' | 'update'): Promise<void> => {
        if (!validateEmploymentHistory(formData, setValue)) {
            errorAlert(t('employees.error-employment-history-validation'));
            return;
        }
        try {
            if (action === 'create') {
                const request = await toCreateRequest(formData);
                // A draft is created by completing it: the employee keeps the draft's id.
                await (draft.draftId === null ? addEmployee(request).unwrap() : draft.complete(request))
                    .then((resp) => {
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveForm(`/employee/${resp.id}`);
                    })
                    .catch(console.error);
            } else if (action === 'update' && employeeId && !isNaN(Number(employeeId))) {
                await uploadProfilePicture(formData);
                const customFieldDtos = mapCustomFieldsToDto(formData as unknown as Record<string, unknown>);
                await updateEmployee(employeeFormToUpdateRequest(formData, customFieldDtos, employeeId))
                    .unwrap()
                    // Only once the employee saved, so a refused save publishes no new picture either.
                    .then(() =>
                        saveWebPicture(formData.webPictureBase64 ?? null, initialData?.webPictureBase64 ?? null)
                    )
                    .then(() => {
                        bypassNavigationBlockerRef.current = true;
                        if (refetch) refetch();
                        reset({}, { keepValues: true });
                        leaveForm(`/employee/${employeeId}`);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreate = () => {
        handleSubmit((formData) => onSubmit(formData, 'create'))();
    };

    const submitUpdate = () => {
        handleSubmit((formData) => onSubmit(formData, 'update'))();
    };

    const { buttonsDisabled } = useSelector((state) => state.buttons);

    if (form.isLoading) {
        return <Loader />;
    }

    // Show inital name if is edit form. Dont do dynamically to prevent unneeded rerenders
    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return formatEmployeeName(
                    { firstName: initialData?.firstName, lastName: initialData?.lastName, onVacation },
                    lastNameFirst
                );
            default:
                return t('employees.add-new-employee');
        }
    };

    const getFormHeader = () => {
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={goBack} />];

        if (type === 'add') {
            actions.push(
                ...(draft.button ? [draft.button] : []),
                <SaveButton
                    key="addSave"
                    permissions={['CREATE.EMPLOYEES']}
                    onClick={submitCreate}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit' && employeeId) {
            actions.push(<VacationButton key="vacation" employeeId={Number(employeeId)} onVacation={onVacation} />);
            actions.push(
                <PermissionButton
                    key="editSave"
                    permissions={['UPDATE.EMPLOYEES', 'UPDATE.EMPLOYEE_PERSONAL.ALL']}
                    color="secondary"
                    variant="contained"
                    size="small"
                    onClick={submitUpdate}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="save" />
                </PermissionButton>
            );
        }

        return <ActionHeader title={getFormTitle()} actions={actions} onBack={goBack} draft={draft.detail} />;
    };

    return (
        <FormProvider {...methods}>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        {getFormHeader()}
                        <DraftSaveMode mode={draft.mode}>
                            <EmploymentHistoryDraftField control={control} />
                            <SectionLayout sectionErrors={sectionErrors}>
                                <Section title="employees.general" isHidden={!showNonPersonalSections}>
                                    <PictureAndNameFields form={form} />
                                    <ActiveAndRolesFields form={form} />
                                    <ContactDetailsFields form={form} />
                                    <EmploymentDetailsFields form={form} />
                                    <PreviousNamesField form={form} />
                                    <TaskReceiversField form={form} />
                                    <CanImpersonateField form={form} />
                                </Section>
                                <Section title="employees.competence" isHidden={!showNonPersonalSections}>
                                    <SubSection title="employees.employment-history">
                                        <EmploymentHistoryFields form={form} />
                                    </SubSection>
                                    <SubSection title="employees.professional-involvement">
                                        <CompetenceFields form={form} />
                                        <PeerGroupField form={form} />
                                    </SubSection>
                                    <SubSection
                                        title="employees.affiliations-and-external-roles"
                                        tooltip="employees.affiliations.tooltip"
                                    >
                                        <AffiliationsFields form={form} />
                                    </SubSection>
                                </Section>
                                {showNonPersonalSections && initialData?.web ? (
                                    <Section title="employees.web.integration">
                                        <WebIntegrationFields form={form} />
                                    </Section>
                                ) : null}
                                {canReadPersonalData ? (
                                    <Section title="employees.personal">
                                        <PersonalFields form={form} />
                                        {showTargets ? (
                                            <SubSection title="employees.targets">
                                                <EmployeeTargetsFields form={form} />
                                            </SubSection>
                                        ) : null}
                                    </Section>
                                ) : null}

                                {customFields?.length > 0 ? (
                                    <CustomFields
                                        control={control as unknown as Control<FieldValues>}
                                        fields={customFields}
                                    />
                                ) : null}
                            </SectionLayout>
                        </DraftSaveMode>
                    </FormWrapper>
                    <ConfirmNavigation
                        control={control}
                        bypassRef={bypassNavigationBlockerRef}
                        draft={type === 'add' ? draft.leave : null}
                    />
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
        </FormProvider>
    );
};

/**
 * Tells a draft save whether the employment history is still missing: no period yet, or one without its job role,
 * start date, position, office or core team, which the create requires.
 */
const EmploymentHistoryDraftField = ({ control }: { control: Control<EmployeeForm> }) => {
    const employmentHistory = useWatch({ control, name: 'employmentHistory' });
    const isEmpty =
        !employmentHistory?.length ||
        employmentHistory.some(
            (item) =>
                !item.jobRole?.id || !item.validFrom || !item.jobPosition?.id || !item.office?.id || !item.coreTeam?.id
        );
    useDraftField(t('employees.employment-history'), true, isEmpty);
    return null;
};

export default EmployeePageForm;
